import { ReactElement } from "react";
import { FieldError } from "react-hook-form";
import { CircleAlert } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Label } from "@shared/ui/Label";
import { Type, TypeVariant } from "@shared/ui/Type";

interface Props {
  id: string;
  label?: string;
  error?: FieldError;
  children: ReactElement;
  icon?: React.ReactNode;
  prefix?: string;
  isLoading?: boolean;
  helpText?: string;
  isSubmitting?: boolean;
  required?: boolean;
  image?: string;
  isCustomComponent?: boolean;
  labelVariant?: TypeVariant;
  className?: string;
  innerClassName?: string;
  isHideErrorMessage?: boolean;
  layout?: "vertical" | "horizontal";
}

export const FormField = ({
  icon,
  prefix,
  id,
  label,
  error,
  children,
  isLoading,
  helpText,
  isSubmitting,
  required,
  isCustomComponent = false,
  image,
  labelVariant,
  className,
  innerClassName,
  isHideErrorMessage = false,
  layout = "vertical",
}: Props) => {
  return (
    <div
      className={twMerge(
        "relative flex flex-col gap-1 transition-opacity duration-100",
        layout === "horizontal" && "md:flex-row md:items-start",
        className,
        (isLoading || isSubmitting) && "opacity-20"
      )}
    >
      {image && (
        <img
          src={image}
          alt={label}
          className="w-32 object-contain mx-auto mb-8"
        />
      )}
      {label && (
        <div
          className={twMerge(
            "flex flex-col gap-2",
            layout === "horizontal" && "mb-0.5 w-full md:mb-0 md:mr-8 md:w-1/3"
          )}
        >
          <Label
            htmlFor={id}
            id={`${id}-label`}
            error={!!error}
            required={required}
            typeVariant={labelVariant}
          >
            {label}
          </Label>
          {helpText && (
            <Type variant="caption" className="text-brand-700">
              {helpText}
            </Type>
          )}
        </div>
      )}
      <div
        className={twMerge(
          "relative flex w-full items-center",
          layout === "horizontal" &&
            "flex w-full flex-col gap-4 md:w-2/3 md:flex-row"
        )}
      >
        {icon && (
          <span className="absolute left-3 top-3 flex items-center w-5 h-5 text-brand-700">
            {icon}
          </span>
        )}
        <div
          className={twMerge(
            "flex flex-col relative w-full gap-1.5",
            prefix && "flex-row gap-0",
            innerClassName
          )}
        >
          {prefix && (
            <span className="bg-brand-50 border-brand-800 text-brand-700 pointer-events-none flex h-10 items-center rounded-bl-sm rounded-tl-sm border border-r-0 py-2 px-3 text-sm md:text-md">
              {prefix}
            </span>
          )}
          {children}
          {!isHideErrorMessage && error && (
            <Type
              variant="caption"
              as="span"
              className="text-error-600 field-error-message"
            >
              {error.message}
            </Type>
          )}
          {error && !isCustomComponent && (
            <CircleAlert
              className="text-error-600 absolute right-3 top-3 h-4 w-4"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FormField;
